import React, { useState, useEffect, useRef, useMemo } from "react";
import Header from "../components/Header";
import Sidebar from "../components/Sidebar";
import { POKUTILS } from "../config/API";
import {
    AlertCircle,
    Building2,
    ChevronLeft,
    Inbox,
    MessageSquare,
    MessagesSquare,
    RefreshCw,
    Search,
    Send,
} from "lucide-react";

const ThreadListSkeleton = () => (
    <div className="space-y-2 p-3">
        {[...Array(4)].map((_, i) => (
            <div key={i} className="animate-pulse rounded-xl border border-gray-100 bg-gray-50 p-4">
                <div className="flex items-start gap-3">
                    <div className="h-10 w-10 shrink-0 rounded-full bg-gray-200" />
                    <div className="flex-1 space-y-2">
                        <div className="h-4 w-3/4 rounded bg-gray-200" />
                        <div className="h-3 w-full rounded bg-gray-100" />
                        <div className="h-5 w-20 rounded-full bg-gray-100" />
                    </div>
                </div>
            </div>
        ))}
    </div>
);

const MessageSkeleton = () => (
    <div className="space-y-4 p-6">
        {[...Array(5)].map((_, i) => (
            <div
                key={i}
                className={`flex ${i % 2 === 0 ? "justify-start" : "justify-end"}`}
            >
                <div
                    className={`animate-pulse rounded-2xl bg-gray-100 ${
                        i % 2 === 0 ? "h-16 w-2/3" : "h-14 w-1/2"
                    }`}
                />
            </div>
        ))}
    </div>
);

const EmptyState = ({ icon: Icon, title, description }) => (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <Icon className="h-8 w-8" strokeWidth={1.5} />
        </div>
        <h3 className="text-base font-semibold text-gray-900">{title}</h3>
        <p className="mt-1.5 max-w-xs text-sm leading-relaxed text-gray-500">{description}</p>
    </div>
);

const isNearBottom = (container, threshold = 100) => {
    if (!container) return true;
    const { scrollTop, scrollHeight, clientHeight } = container;
    return scrollHeight - scrollTop - clientHeight < threshold;
};

const MyThread = () => {
    const [threads, setThreads] = useState([]);
    const [selectedThread, setSelectedThread] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [loadingThreads, setLoadingThreads] = useState(true);
    const [loadingMessages, setLoadingMessages] = useState(false);
    const [refreshingThreads, setRefreshingThreads] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState(null);
    const [fullName, setFullName] = useState("");

    const chatContainerRef = useRef(null);
    const messagesIntervalRef = useRef(null);
    const inputRef = useRef(null);
    const scrollOnNextUpdateRef = useRef(false);

    useEffect(() => {
        const storedFullName = sessionStorage.getItem("fullname");
        setFullName(storedFullName || "");
    }, []);

    const formatMessageDate = (dateInput) => {
        try {
            if (typeof dateInput === "string") {
                return dateInput.replace(/(:\d{2})( [AP]M)/, "$2");
            }

            const date = new Date(dateInput);
            const month = date.getMonth() + 1;
            const day = date.getDate();
            const year = date.getFullYear();
            let hours = date.getHours();
            const minutes = date.getMinutes().toString().padStart(2, "0");
            const ampm = hours >= 12 ? "PM" : "AM";
            hours = hours % 12 || 12;

            return `${month}/${day}/${year} ${hours}:${minutes} ${ampm}`;
        } catch {
            return "Just now";
        }
    };

    const getThreadId = (thread) =>
        thread?.itemid ?? thread?.threadid ?? thread?.ThreadID ?? thread?.id ?? thread?.ID;

    const getThreadTitle = (thread) =>
        thread?.name ?? thread?.Name ?? `Thread ${getThreadId(thread)}`;

    const getThreadInitials = (title) =>
        (title || "?")
            .split(" ")
            .slice(0, 2)
            .map((word) => word[0])
            .join("")
            .toUpperCase();

    const filteredThreads = useMemo(() => {
        const query = searchQuery.trim().toLowerCase();
        if (!query) return threads;

        return threads.filter((thread) => {
            const title = getThreadTitle(thread).toLowerCase();
            const description = (thread.description || "").toLowerCase();
            const department = (thread.department || "").toLowerCase();
            return title.includes(query) || description.includes(query) || department.includes(query);
        });
    }, [threads, searchQuery]);

    const fetchThreads = async () => {
        const params = { action: "threads" };
        try {
            const response = await fetch(POKUTILS, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    Accept: "application/json",
                },
                body: new URLSearchParams(params),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            if (Array.isArray(data)) {
                setThreads(data);
            } else if (data?.status === "error") {
                throw new Error(data.message || "Failed to load threads");
            } else {
                setThreads([]);
            }
        } catch (err) {
            setError(err.message);
        }
    };

    const fetchThreadMessages = async (threadId, silent = false) => {
        if (!threadId) return;

        if (!silent) {
            setLoadingMessages(true);
        }

        try {
            const params = {
                action: "threadmessages",
                threadid: String(threadId),
            };
            const response = await fetch(POKUTILS, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    Accept: "application/json",
                },
                body: new URLSearchParams(params),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            if (Array.isArray(data)) {
                const formattedMessages = data.map((msg, index) => ({
                    id: msg.id ?? msg.messageid ?? Date.now() + index,
                    sender: msg.name ?? msg.sender ?? msg.from ?? "Unknown",
                    text: msg.messagetext ?? msg.message ?? msg.msg ?? msg.text ?? "",
                    time: formatMessageDate(msg.messagedate ?? msg.date ?? msg.time),
                    isMe: (msg.name ?? msg.sender ?? msg.from) === fullName,
                    avatar: (msg.name ?? msg.sender ?? msg.from ?? "?")
                        .split(", ")
                        .map((part) => part[0])
                        .join(""),
                }));

                scrollOnNextUpdateRef.current =
                    !silent || isNearBottom(chatContainerRef.current);
                setMessages(formattedMessages.reverse());
            } else if (data?.status === "error") {
                throw new Error(data.message || "Failed to load messages");
            } else {
                scrollOnNextUpdateRef.current = !silent;
                setMessages([]);
            }
        } catch (err) {
            if (!silent) {
                setError(err.message);
            }
        } finally {
            if (!silent) {
                setLoadingMessages(false);
            }
        }
    };

    useEffect(() => {
        const loadThreads = async () => {
            setLoadingThreads(true);
            setError(null);
            try {
                await fetchThreads();
            } finally {
                setLoadingThreads(false);
            }
        };

        loadThreads();
    }, []);

    useEffect(() => {
        if (!selectedThread) {
            if (messagesIntervalRef.current) {
                clearInterval(messagesIntervalRef.current);
                messagesIntervalRef.current = null;
            }
            return;
        }

        const threadId = getThreadId(selectedThread);
        setError(null);
        fetchThreadMessages(threadId);

        messagesIntervalRef.current = setInterval(() => {
            fetchThreadMessages(threadId, true);
        }, 5000);

        return () => {
            if (messagesIntervalRef.current) {
                clearInterval(messagesIntervalRef.current);
            }
        };
    }, [selectedThread, fullName]);

    useEffect(() => {
        if (!scrollOnNextUpdateRef.current || !chatContainerRef.current) return;

        chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        scrollOnNextUpdateRef.current = false;
    }, [messages]);

    const handleRefreshThreads = async () => {
        setRefreshingThreads(true);
        setError(null);
        try {
            await fetchThreads();
        } finally {
            setRefreshingThreads(false);
        }
    };

    const handleSelectThread = (thread) => {
        setSelectedThread(thread);
        setMessages([]);
        setNewMessage("");
        setError(null);
    };

    const handleBackToThreads = () => {
        setSelectedThread(null);
        setMessages([]);
        setNewMessage("");
        setError(null);
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        const trimmed = newMessage.trim();
        if (!trimmed || !selectedThread || sending) return;

        const threadId = getThreadId(selectedThread);
        const tempId = Date.now();
        const optimisticMessage = {
            id: tempId,
            sender: "You",
            text: trimmed,
            time: formatMessageDate(new Date()),
            isMe: true,
        };

        scrollOnNextUpdateRef.current = true;
        setMessages((prev) => [...prev, optimisticMessage]);
        setNewMessage("");
        setSending(true);
        setError(null);

        try {
            const params = {
                action: "sendthreadmessage",
                threadid: String(threadId),
                msg: trimmed,
            };
            const response = await fetch(POKUTILS, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    Accept: "application/json",
                },
                body: new URLSearchParams(params),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error("Failed to send message");
            }
            if (result?.status === "error") {
                throw new Error(result.message || "Failed to send message");
            }

            setMessages((prev) =>
                prev.map((msg) =>
                    msg.id === tempId ? { ...msg, id: result.id ?? tempId } : msg
                )
            );

            await fetchThreadMessages(threadId, true);
        } catch (err) {
            setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
            setNewMessage(trimmed);
            setError(err.message || "Failed to send message. Please try again.");
        } finally {
            setSending(false);
            inputRef.current?.focus();
        }
    };

    const renderThreadList = () => (
        <div className="flex h-full flex-col bg-white">
            <div className="relative border-b border-gray-100 bg-gradient-to-r from-primary/5 via-white to-white px-4 py-4 sm:px-5">
                <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-primary via-primary/80 to-primary/40" />
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                            <MessagesSquare className="h-4 w-4" strokeWidth={2} />
                        </div>
                        <div>
                            <h2 className="text-base font-bold tracking-tight text-gray-900 sm:text-lg">
                                My Threads
                            </h2>
                            <p className="text-xs text-gray-500 sm:text-sm">
                                {threads.length} {threads.length === 1 ? "conversation" : "conversations"}
                            </p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={handleRefreshThreads}
                        disabled={refreshingThreads || loadingThreads}
                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 shadow-sm transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary disabled:cursor-not-allowed disabled:opacity-50"
                        aria-label="Refresh threads"
                    >
                        <RefreshCw
                            className={`h-4 w-4 ${refreshingThreads ? "animate-spin" : ""}`}
                        />
                    </button>
                </div>

                <div className="relative mt-4">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search threads..."
                        className="w-full rounded-xl border border-gray-200 bg-gray-50/80 py-2.5 pl-10 pr-4 text-sm text-gray-700 placeholder-gray-400 transition focus:border-primary/30 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/10"
                    />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-3">
                {loadingThreads ? (
                    <ThreadListSkeleton />
                ) : filteredThreads.length === 0 ? (
                    <EmptyState
                        icon={searchQuery ? Search : Inbox}
                        title={searchQuery ? "No matching threads" : "No threads yet"}
                        description={
                            searchQuery
                                ? "Try a different search term or clear the filter."
                                : "When threads are assigned to you, they will appear here."
                        }
                    />
                ) : (
                    <ul className="space-y-2">
                        {filteredThreads.map((thread) => {
                            const threadId = getThreadId(thread);
                            const title = getThreadTitle(thread);
                            const isSelected = getThreadId(selectedThread) === threadId;

                            return (
                                <li key={threadId}>
                                    <button
                                        type="button"
                                        onClick={() => handleSelectThread(thread)}
                                        className={`group w-full rounded-xl border p-4 text-left transition-all ${
                                            isSelected
                                                ? "border-primary/30 bg-primary/5 shadow-sm ring-1 ring-primary/20"
                                                : "border-gray-100 bg-white hover:border-gray-200 hover:bg-gray-50/80 hover:shadow-sm"
                                        }`}
                                    >
                                        <div className="flex items-start gap-3">
                                            <div
                                                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
                                                    isSelected
                                                        ? "bg-primary text-white"
                                                        : "bg-primary/10 text-primary group-hover:bg-primary/15"
                                                }`}
                                            >
                                                {getThreadInitials(title)}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate font-semibold text-gray-900">
                                                    {title}
                                                </p>
                                                {thread.description && (
                                                    <p className="mt-1 line-clamp-2 text-sm leading-snug text-gray-500">
                                                        {thread.description}
                                                    </p>
                                                )}
                                                {thread.department && (
                                                    <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                                                        <Building2 className="h-3 w-3" />
                                                        {thread.department}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </div>
    );

    const renderConversation = () => {
        if (!selectedThread) {
            return (
                <div className="hidden flex-1 flex-col items-center justify-center bg-gradient-to-b from-gray-50/50 to-white md:flex">
                    <EmptyState
                        icon={MessageSquare}
                        title="Select a thread"
                        description="Choose a conversation from the list to view messages and reply."
                    />
                </div>
            );
        }

        const threadTitle = getThreadTitle(selectedThread);

        return (
            <div className="flex h-full flex-col bg-white">
                <div className="relative border-b border-gray-100 bg-gradient-to-r from-white via-white to-primary/5 px-4 py-4 sm:px-5">
                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            onClick={handleBackToThreads}
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm transition hover:border-primary/30 hover:bg-primary/5 hover:text-primary md:hidden"
                            aria-label="Back to threads"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </button>

                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-white">
                            {getThreadInitials(threadTitle)}
                        </div>

                        <div className="min-w-0 flex-1">
                            <h2 className="truncate text-base font-bold tracking-tight text-gray-900 sm:text-lg">
                                {threadTitle}
                            </h2>
                            <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-500 sm:text-sm">
                                {selectedThread.description && (
                                    <span className="truncate">{selectedThread.description}</span>
                                )}
                                {selectedThread.department && (
                                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary">
                                        <Building2 className="h-3 w-3" />
                                        {selectedThread.department}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    ref={chatContainerRef}
                    className="flex-1 overflow-y-auto bg-[linear-gradient(to_bottom,#f9fafb_0%,#ffffff_100%)] px-4 py-5 sm:px-6"
                >
                    {error && (
                        <div className="mb-4 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                            <div className="flex-1">
                                <p className="font-medium">Something went wrong</p>
                                <p className="mt-0.5 text-red-600/90">{error}</p>
                            </div>
                        </div>
                    )}

                    {loadingMessages ? (
                        <MessageSkeleton />
                    ) : messages.length === 0 ? (
                        <EmptyState
                            icon={MessageSquare}
                            title="No messages yet"
                            description="Start the conversation by sending a message below."
                        />
                    ) : (
                        <div className="space-y-4">
                            {messages.map((message) => (
                                <div
                                    key={message.id}
                                    className={`flex ${message.isMe ? "justify-end" : "justify-start"} items-end gap-2.5`}
                                >
                                    {!message.isMe && (
                                        <div className="mb-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                                            {message.avatar}
                                        </div>
                                    )}
                                    <div
                                        className={`max-w-[min(85%,28rem)] rounded-2xl px-4 py-3 shadow-sm ${
                                            message.isMe
                                                ? "rounded-br-md bg-primary text-white"
                                                : "rounded-bl-md border border-gray-100 bg-white text-gray-800"
                                        }`}
                                    >
                                        {!message.isMe && (
                                            <p className="mb-1 text-xs font-semibold text-primary">
                                                {message.sender}
                                            </p>
                                        )}
                                        <p
                                            className={`whitespace-pre-wrap text-sm leading-relaxed ${
                                                message.isMe ? "text-white" : "text-gray-700"
                                            }`}
                                        >
                                            {message.text}
                                        </p>
                                        <p
                                            className={`mt-1.5 text-[11px] ${
                                                message.isMe ? "text-primary-100/90" : "text-gray-400"
                                            }`}
                                        >
                                            {message.time}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <form
                    onSubmit={handleSendMessage}
                    className="border-t border-gray-100 bg-white px-4 py-4 sm:px-5"
                >
                    <div className="flex items-end gap-2 rounded-2xl border border-gray-200 bg-gray-50/60 p-2 shadow-sm transition focus-within:border-primary/30 focus-within:bg-white focus-within:ring-2 focus-within:ring-primary/10">
                        <input
                            ref={inputRef}
                            type="text"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="Write your reply..."
                            disabled={sending}
                            className="min-h-[44px] flex-1 bg-transparent px-3 py-2.5 text-sm text-gray-800 placeholder-gray-400 focus:outline-none disabled:cursor-not-allowed"
                        />
                        <button
                            type="submit"
                            disabled={sending || !newMessage.trim()}
                            className="flex h-11 shrink-0 items-center gap-2 rounded-xl bg-primary px-4 text-sm font-medium text-white shadow-sm transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50 sm:px-5"
                        >
                            {sending ? (
                                <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                            ) : (
                                <>
                                    <Send className="h-4 w-4" />
                                    <span className="hidden sm:inline">Send</span>
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        );
    };

    return (
        <div className="flex h-screen flex-col bg-gray-50">
            <div className="fixed left-0 top-0 z-50 w-full bg-primary shadow-sm">
                <Header />
            </div>

            <div className="mt-[72px] flex flex-1">
                <div className="fixed left-0 hidden h-[calc(100vh-72px)] w-64 overflow-y-auto border-r border-gray-200 bg-white lg:block">
                    <Sidebar />
                </div>

                <main className="mt-[25px] flex-1 overflow-hidden p-4 sm:p-6 lg:ml-64">
                    <div className="mx-auto w-full max-w-6xl">
                        <div className="mb-4 hidden sm:block">
                            <h1 className="text-xl font-bold tracking-tight text-gray-900">
                                Thread Messages
                            </h1>
                            <p className="mt-1 text-sm text-gray-500">
                                View and respond to your department conversations
                            </p>
                        </div>

                        <div
                            className="overflow-hidden rounded-2xl border border-gray-200/80 bg-white shadow-sm"
                            style={{ height: "calc(100vh - 200px)" }}
                        >
                            <div className="flex h-full">
                                <div
                                    className={`w-full shrink-0 border-r border-gray-100 md:w-[340px] lg:w-[380px] ${
                                        selectedThread ? "hidden md:flex md:flex-col" : "flex flex-col"
                                    }`}
                                >
                                    {renderThreadList()}
                                </div>

                                <div
                                    className={`min-w-0 flex-1 ${
                                        selectedThread ? "flex flex-col" : "hidden md:flex md:flex-col"
                                    }`}
                                >
                                    {renderConversation()}
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default MyThread;
